import axios, { AxiosError } from 'axios'
import isEmpty from 'lodash/isEmpty'
import { buildQueryParams } from './common/utils/queryParams'
import httpStatuses from './httpStatusCodes'
import { tokenService } from '@app/auth/tokenService'
import { contextOriginService } from '@app/auth/contextOriginService'
import { HEADER_REGGORA_ORIGIN } from '@config/headers'
import { tokenRefreshService } from '@app/auth/token-refresh-service'

/**
 * @returns {Promise<string|undefined>} The authorization header value or `undefined` if no token is available
 */
const getAuthorizationHeader = async () => {
  const token = await tokenService.getTokenAsync()
  return token ? `bearer ${token}` : undefined
}

const queryParamSerializer = (params) => {
  return isEmpty(params) ? '' : '?' + buildQueryParams(params)
}

export class ApiClient {
  constructor(baseUrl) {
    this.client = axios.create({
      baseURL: baseUrl,
    })
    this.client.interceptors.request.use(
      async (config) => {
        const authorizationHeader = await getAuthorizationHeader()
        if (authorizationHeader) {
          config.headers = {
            ...config.headers,
            Authorization: authorizationHeader,
          }
        }
        const reggora_origin_context = contextOriginService.getToken()
        if (reggora_origin_context) {
          config.headers[HEADER_REGGORA_ORIGIN] = reggora_origin_context
        }
        return config
      },
      (error) => ApiClient.requestErrorHandler(error),
    )
    // By default, axios throws an error if the response status is outside the range of 200-299
    this.client.interceptors.response.use(
      (res) => res.data,
      async (error) => {
        const status = error?.response?.status
        if (status !== 401) {
          return ApiClient.responseErrorHandler(error)
        }

        const originalRequest = error.config
        if (originalRequest._retried) {
          return ApiClient.responseErrorHandler(error)
        }
        originalRequest._retried = true

        try {
          await tokenRefreshService.attemptRefresh()
        } catch (refreshError) {
          return Promise.reject(error)
        }

        const token = tokenService.getToken()
        if (token) {
          originalRequest.headers.Authorization = `bearer ${token}`
        }
        return this.client.request(originalRequest)
      },
    )
  }

  /**
   * Sends a `GET` request.
   * @param {string} url - The endpoint URL to send the request to
   * @param {Object} [queryParams={}] - Query parameters to append to the URL
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  async get(url, queryParams = {}, config = {}) {
    const serializedQueryParams = queryParamSerializer(queryParams)
    const requestUrl = `${url}${serializedQueryParams}`
    return this.client.get(requestUrl, config)
  }

  /**
   * Sends a `POST` request using an `application/json` body.
   * @param {string} url - The endpoint URL to send the request to
   * @param {Object} [queryParams={}] - Query parameters to append to the URL
   * @param {Object} [body={}] - The request body to send
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  post(url, queryParams = {}, body = {}, config = {}) {
    const serializedQueryParams = queryParamSerializer(queryParams)
    const requestUrl = `${url}${serializedQueryParams}`
    return this.client.post(requestUrl, body, config)
  }

  /**
   * Sends a `POST` request using an `multipart/form-data` body.
   * @param {string} url - The endpoint URL to send the request to
   * @param {Object} [queryParams={}] - Query parameters to append to the URL
   * @param {Object} [body={}] - The request body to send
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  postForm(url, queryParams = {}, body = {}, config = {}) {
    const serializedQueryParams = queryParamSerializer(queryParams)
    const requestUrl = `${url}${serializedQueryParams}`
    return this.client.postForm(requestUrl, body, config)
  }

  /**
   * Sends a `PUT` request using an `application/json` body.
   * @param {string} url - The endpoint URL to send the request to
   * @param {Object} [queryParams={}] - Query parameters to append to the URL
   * @param {Object} [body={}] - The request body to send
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  put(url, queryParams = {}, body = {}, config = {}) {
    const serializedQueryParams = queryParamSerializer(queryParams)
    const requestUrl = `${url}${serializedQueryParams}`
    return this.client.put(requestUrl, body, config)
  }

  /**
   * Sends a `PUT` request using an `multipart/form-data` body.
   * @param {string} url - The endpoint URL to send the request to
   * @param {Object} [queryParams={}] - Query parameters to append to the URL
   * @param {Object} [body={}] - The request body to send
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  putForm(url, queryParams = {}, body = {}, config = {}) {
    const serializedQueryParams = queryParamSerializer(queryParams)
    const requestUrl = `${url}${serializedQueryParams}`
    return this.client.putForm(requestUrl, body, config)
  }

  /**
   * Sends a `PATCH` request using an `application/json` body.
   * @param {string} url - The endpoint URL to send the request to
   * @param {Object} [queryParams={}] - Query parameters to append to the URL
   * @param {Object} [body={}] - The request body to send
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  patch(url, queryParams = {}, body = {}, config = {}) {
    const serializedQueryParams = queryParamSerializer(queryParams)
    const requestUrl = `${url}${serializedQueryParams}`
    return this.client.patch(requestUrl, body, config)
  }

  /**
   * Sends a `PATCH` request using an `multipart/form-data` body.
   * @param {string} url - The endpoint URL to send the request to
   * @param {Object} [queryParams={}] - Query parameters to append to the URL
   * @param {Object} [body={}] - The request body to send
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  patchForm(url, queryParams = {}, body = {}, config = {}) {
    const serializedQueryParams = queryParamSerializer(queryParams)
    const requestUrl = `${url}${serializedQueryParams}`
    return this.client.patchForm(requestUrl, body, config)
  }

  /**
   * Sends an `OPTIONS` request.
   * @param {string} url - The endpoint URL to send the request to
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  options(url, config = {}) {
    return this.client.options(url, config)
  }

  /**
   * Sends a `DELETE` request.
   * @param {string} url - The endpoint URL to send the request to
   * @param {import('axios').AxiosRequestConfig} [config={}] - Additional axios request configuration
   * @returns {Promise<any>}
   */
  delete(url, config = {}) {
    return this.client.delete(url, config)
  }

  static requestErrorHandler(error) {
    if (error instanceof AxiosError) {
      // console.warn(`API request error: ${error.message}`, error.toJSON())
      return Promise.reject(error)
    } else {
      console.error('API request error: Unexpected error', error)
      return Promise.reject(new Error('An unexpected error occurred while processing the request.'))
    }
  }

  static responseErrorHandler(error) {
    if (error instanceof AxiosError) {
      // console.warn(`API response error: ${error.message}`, error.toJSON())
      return Promise.reject(error)
    } else {
      console.error('API response error: Unexpected error', error)
      return Promise.reject(new Error('An unexpected error occurred while processing the response.'))
    }
  }
}

export const statusCodes = httpStatuses

/**
 * Creates a new ApiClient instance with the specified base URL.
 * @param {string} baseUrl
 * @returns {ApiClient}
 */
export default function(baseUrl) {
  return new ApiClient(baseUrl)
}
